import { useEffect, useRef, useState } from 'react';
import { TextInput } from '../helpers/TextInput';
import { Dropdown } from '../helpers/Dropdown';
import { Avatar as MuiAvatar } from '@mui/material';
import { useAuthContext } from '../../store/AuthContext';
import { updateUserSettings } from '../../services/userService';
import { getClientLocales } from '../../services/clientService';
import { fetchLocales } from '../../services/systemService';
import { uploadFile } from '../../services/containerService';
import { useUploadContext } from '../../store/Contexts';
import { getUserPreferences } from '../../services/individualService';
import { isValidFileTypeAndSize } from '../../utils/validate';
import style from '../ui/modals/modals.module.scss';
import Toast from '../ui/toasts/Toast';
import ReactAvatarEditor from 'react-avatar-editor';
import { TenantTiers } from '../../utils/data/enums';
import { getText } from '../../i18n';

const UserInfo = ({
    onYes = null,
    onNo = null,
    onHide = null,
    isModal = false,
}) => {
    const { user, setUser, multitenantFlag, clientTierMeetsRequirement } = useAuthContext();
    const { enqueueUpload } = useUploadContext();
    const editor = useRef(null);
    const [file, setFile] = useState();
    const [showPreview, setShowPreview] = useState(false);
    const [preferredName, setPreferredName] = useState(user.preferred_name);
    const [isLoading, setIsLoading] = useState(true);
    const [pronouns, setPronouns] = useState(
        user.pronouns || 'Prefer not to say',
    );
    const [preferences, setPreferences] = useState({
        fileEmailAddress: '',
        emailAddress: '',
    });
    const [selectedLoginEmail, setSelectedLoginEmail] = useState('');
    const [languageCode, setLanguageCode] = useState(user.locale ?? 'English');

    const [avatarURL, setAvatarURL] = useState(user.avatar_url ?? '');
    const [scale, setScale] = useState(1);
    const [supportedLanguages, setSupportedLanguages] = useState();
    const [defaultLanguage, setDefaultLanguage] = useState();

    const language =
        supportedLanguages?.find(lang => lang.code === languageCode)?.name ??
        defaultLanguage?.name;

    // Filter to only selected languages, then map to desired structure
    const selectedLanguages = (supportedLanguages ?? []).map(lang => ({
        label: lang?.name,
        value: lang?.code,
    }));

    // Define the pronoun list
    const pronounList = [
        { value: 'Prefer not to say', label: getText('msg_0e4b2056bcb8') },
        { value: 'he/him', label: getText('msg_3c9101dc2631') },
        { value: 'she/her', label: getText('msg_da5be324c587') },
        { value: 'they/them', label: getText('msg_c44051d45902') },
        { value: 'ze/hir', label: getText('msg_7bafa6fe3d7b') },
    ];

    useEffect(() => {
        loadUserPreferences();
        getOptions();
    }, []);

    const loadUserPreferences = async () => {
        try {
            setIsLoading(true);
            const userPrefs = await getUserPreferences(
                user.jwt.ind ?? 0,
                user.id,
                user.locale
            );
            setPreferences({
                fileEmailAddress: userPrefs.fileEmailAddress,
                emailAddress: userPrefs.emailAddress,
            });
            setSelectedLoginEmail(userPrefs.username);
            setLanguageCode(userPrefs.locale);
        } catch (error) {
            Toast.error(getText('msg_e434781fe6a7'));
        } finally {
            setIsLoading(false);
        }
    };

    const getOptions = async () => {
        try {
            //GET
            const [allSystemLanguages, tenantLocales] = await Promise.all([
                fetchLocales(),
                getClientLocales(),
            ]);

            const systemLanguages = (allSystemLanguages ?? []).map(lang => ({
                name: lang.name,
                code: lang.code,
                value: lang.code,
                id: lang.id,
                isDefault: lang.isDefault,
            }));

            const defaultSystemLanguage = systemLanguages.find(
                l => l.isDefault,
            );
            setDefaultLanguage(defaultSystemLanguage);

            if (multitenantFlag) setSupportedLanguages([defaultSystemLanguage]);
            // if there are tenant specified locales
            else if (tenantLocales && tenantLocales?.length > 0) {
                const locales = [];

                tenantLocales.map(tenantLocale => {
                    var locale = systemLanguages.find(
                        l => l.id === tenantLocale.localeId,
                    );
                    locales.push(locale);
                });

                setSupportedLanguages(locales);
                // just return english
            } else {
                setSupportedLanguages([defaultSystemLanguage]);
            }
        } catch (error) {
            Toast.error(getText('msg_42234d7fd733'));
            console.error('Could not fetch:', error);
        }
    };

    const handleYes = async () => {
        try {
            setIsLoading(true);
            const res = await updateUserSettings(
                languageCode,
                preferredName,
                pronouns,
                avatarURL,
                selectedLoginEmail,
            );
            setUser({
                ...user,
                locale: res.locale,
                preferred_name: res.preferred_name,
                pronouns: res.pronouns,
                avatar_url: res.avatar_url,
                username: selectedLoginEmail,
            });
            if (isModal) {
                onYes();
            }
            if (onHide) {
                onHide();
            }
            Toast.success(getText('msg_0662dc672242'));
        } catch (error) {
            Toast.error(getText('msg_400ca1a999ba'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleNo = () => {
        onNo();
        onHide();
    };

    const selectFile = event => {
        event.preventDefault();
        setShowPreview(true);
        const newFile = event.target.files[0];
        setFile(newFile);
        document.getElementById('imageInput').value = null;
    };

    const handleYesImage = async () => {
        try {
            const successMessage = getText('msg_408bdb4908eb');
            if (!file) {
                Toast.success(successMessage);
                setShowPreview(false);
                setScale(1);
                return;
            }
            const dataUrl = editor.current.getImage().toDataURL();
            const dataUrlResponse = await fetch(dataUrl);
            const blob = await dataUrlResponse.blob();
            const croppedFile = new File([blob], file.name, {
                type: blob.type,
            });
            const fileTypeAndSizeValidation =
                isValidFileTypeAndSize(croppedFile);
            if (fileTypeAndSizeValidation)
                return Toast.error(fileTypeAndSizeValidation);
            const uploadFileResponse = await enqueueUpload(
                croppedFile.name,
                uploadOptions => uploadFile(croppedFile, uploadOptions),
            );
            const res = await updateUserSettings(
                null,
                null,
                null,
                uploadFileResponse,
            );
            setUser({
                ...user,
                locale: res.locale,
                preferred_name: res.preferred_name,
                pronouns: res.pronouns,
                avatar_url: res.avatar_url,
            });
            setAvatarURL(uploadFileResponse);
            setFile(null);
            setShowPreview(false);
            setScale(1);
            Toast.success(successMessage);
        } catch (error) {
            Toast.error(getText('msg_20662710b5e7'));
        }
    };

    // Define the deleteImage function
    const deleteImage = async () => {
        try {
            const res = await updateUserSettings(null, null, null, '');
            setUser({
                ...user,
                locale: res.locale,
                preferred_name: res.preferred_name,
                pronouns: res.pronouns,
                avatar_url: res.avatar_url,
            });
            setAvatarURL('');
            Toast.success(getText('msg_699ef42197ea'));
        } catch (error) {
            Toast.error(getText('msg_e3d03a72c872'));
        }
    };

    const handleLanguageChange = value => {
        setLanguageCode(value);
    };

    return (
        <div className={`modal-body text-center ${style.modalBody}`}>
            <div className="display-flex justify-content-center">
                {showPreview ? (
                    <div
                        className={`width-full ${style.profileColumnSeparator} display-flex field-separator align-items-center flex-direction-column`}
                    >
                        <ReactAvatarEditor
                            width={250}
                            height={250}
                            image={file ?? ''}
                            border={1}
                            borderRadius={125}
                            disableBoundaryChecks={true}
                            scale={scale}
                            ref={editor}
                            className={`${style.reactAvatarEditor}`}
                        />
                        <div className={`${style.zoomRange}`}>
                            <input
                                type="range"
                                className="form-range"
                                onChange={event => setScale(event.target.value)}
                                min="0.5"
                                max="2"
                                step="0.01"
                                value={scale}
                            />
                        </div>
                        <span
                            className={`text-button mx-3 ${style.profileFormatsSeparator}`}
                        >
                            {getText('msg_3c64634a5394')}
                        </span>
                        <div>
                            <input
                                type="file"
                                id="imageInput"
                                onChange={selectFile}
                                hidden
                            />
                            <label
                                type="button"
                                onClick={() => setFile(null)}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-trash3 me-2" /> {getText('msg_f6fdbe48dc54')}
                            </label>
                            <label
                                type="button"
                                htmlFor="imageInput"
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-cloud-upload me-2" /> {getText('msg_8bdf057f91e7')}
                            </label>
                        </div>
                    </div>
                ) : (
                    <div className="width-half">
                        <div className="width-full display-flex field-separator align-items-center flex-direction-column">
                            <MuiAvatar
                                sx={{
                                    width: 200,
                                    height: 200,
                                    fontSize: 'xxx-large',
                                    marginBottom: '12px',
                                }}
                                src={
                                    avatarURL !== ''
                                        ? avatarURL
                                        : (user.avatar_url ?? '')
                                }
                            >
                                {user.first_name[0] + user.last_name[0]}
                            </MuiAvatar>
                            <div>
                                <input
                                    type="file"
                                    id="imageInput"
                                    onChange={selectFile}
                                    hidden
                                />
                                <label
                                    type="button"
                                    onClick={deleteImage}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" /> {getText('msg_f6fdbe48dc54')}
                                </label>
                                <label
                                    type="button"
                                    htmlFor="imageInput"
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-cloud-upload me-2" />{' '}
                                    {getText('msg_8bdf057f91e7')}
                                </label>
                            </div>
                            <span
                                className={`m-3 ${style.profileFormatsSeparator}`}
                                style={{
                                    fontWeight: '500',
                                    fontSize: '0.875em',
                                    color: '#667085',
                                }}
                            >
                                {getText('msg_3c64634a5394')}
                            </span>
                        </div>
                        <div className="mb-4">
                            <div
                                className="file-form-label text-align-left"
                                style={{ color: '#667085' }}
                            >
                                {getText('msg_64346b483c0a')}
                            </div>
                            <p
                                className="text-align-left"
                                style={{
                                    color: '#2F2B43',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                }}
                            >
                                {user.first_name + ' ' + user.last_name}
                            </p>
                        </div>
                        <div className="field-separator">
                            <div className="file-form-label text-align-left">
                                {getText('msg_6df7d8380ffc')}
                            </div>
                            <TextInput
                                OnChange={setPreferredName}
                                Value={preferredName}
                            />
                        </div>
                        <div className="text-align-left field-separator">
                            <div className="file-form-label">{getText('msg_8a2abafdb920')}</div>
                            <Dropdown
                                defaultValue={pronouns}
                                onChange={setPronouns}
                                options={pronounList}
                            />
                        </div>
                        {clientTierMeetsRequirement(TenantTiers.Silver) &&
                            (
                                <div className="text-align-left field-separator">
                                    <div className="file-form-label">{getText('msg_89b86ab0e66f')}</div>
                                    <Dropdown
                                        defaultValue={language}
                                        onChange={handleLanguageChange}
                                        options={selectedLanguages}
                                    />
                                </div>)}
                    </div>
                )}
            </div>
            <div className={`d-flex justify-content-evenly mt-5`}>
                {isModal && (
                    <button
                        type="button"
                        className="ing-popup-blue-negative"
                        onClick={handleNo}
                    >
                        {getText('msg_77dfd2135f4d')}
                    </button>
                )}
                <button
                    type="button"
                    className="ing-button-primary-none"
                    onClick={showPreview ? handleYesImage : handleYes}
                >
                    {getText('msg_efc007a393f6')}
                </button>
            </div>
        </div>
    );
};

export default UserInfo;
